import { useCallback, useEffect, useId, useRef, useState } from "react";

import { toast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { useFetchRelatedData } from "~/pages/purchase-orders/hooks/salesRequests/useFetchRelatedData";
import {
  FetchResult,
  useFetchSalesRequests,
} from "~/pages/purchase-orders/hooks/salesRequests/useFetchSalesRequests";
import { SalesRequest, SalesRequestItem } from "~/types/client";
import { chunkArray } from "~/utils/arrayUtils";

export const useSalesRequests = (viewAllSellers: boolean = false) => {
  // Unique ID for this hook instance to prevent Realtime channel name collisions
  const instanceId = useId();

  const [salesRequests, setSalesRequests] = useState<SalesRequest[]>([]);
  const [salesRequestItems, setSalesRequestItems] = useState<
    Record<string, SalesRequestItem[]>
  >({});
  const [clientRiskStatus, setClientRiskStatus] = useState<
    Record<string, boolean>
  >({});
  const [clientSalesConditions, setClientSalesConditions] = useState<
    Record<string, string>
  >({});
  const [paymentCounts, setPaymentCounts] = useState<Record<string, number>>(
    {}
  );
  // Sales requests whose status just changed, mapped to their destination
  // status — drives the Kanban move animation. Flagged at realtime-event time
  // (before any refetch); the Kanban only animates a card once its own status
  // matches the destination, so it plays in the new column, not the old one.
  const [movedStatuses, setMovedStatuses] = useState<Map<string, string>>(
    new Map()
  );

  const {
    isLoading,
    isLoadingMore,
    hasMore,
    fetchSalesRequests: fetchRequests,
  } = useFetchSalesRequests(setSalesRequests, viewAllSellers);

  const {
    clientNames,
    clientInfo,
    sellerProfiles,
    approverProfiles,
    fetchClientNames,
    fetchSellerProfiles,
    fetchApproverProfiles,
    fetchSalesRequestItems,
  } = useFetchRelatedData(setSalesRequestItems);

  const getSellerInfo = (sellerId: string) => {
    const seller = sellerProfiles[sellerId] || {};
    return {
      name: seller.name || "Vendedor desconocido",
      lastname: seller.lastname || "",
      email: seller.email || "Sin email",
    };
  };

  const getApproverRejecterInfo = (approverId?: string) => {
    if (!approverId) {
      return {
        name: "Sistema",
        lastname: "",
        email: "",
        role: "system",
      };
    }

    const approver = approverProfiles[approverId] || {};
    return {
      name: approver.name || "Usuario desconocido",
      lastname: approver.lastname || "",
      email: approver.email || "",
      role: approver.role || "unknown",
    };
  };

  // NOTE: fetchClientRiskStatus and fetchClientSalesConditions were removed
  // Client data is now fetched in the initial query to eliminate N+1 queries

  const fetchPaymentCounts = useCallback(async (requests: SalesRequest[]) => {
    const salesRequestIds = requests.map((request) => request.id);

    if (salesRequestIds.length === 0) return;

    try {
      // Batch the requests to avoid URL length limits
      const batches = chunkArray(salesRequestIds, 100);
      const paymentCountsMap: Record<string, number> = {};

      for (const batch of batches) {
        const { data, error } = await supabase
          .from("payments")
          .select("sales_request")
          .in("sales_request", batch);

        if (error) throw error;

        if (data) {
          // Count payments for each sales request
          batch.forEach((salesRequestId) => {
            const count = data.filter(
              (payment) => payment.sales_request === salesRequestId
            ).length;
            paymentCountsMap[salesRequestId] = count;
          });
        }
      }

      setPaymentCounts(paymentCountsMap);
    } catch (error) {
      console.error("Error fetching payment counts:", error);
    }
  }, []);

  const fetchSalesRequests = useCallback(
    async (isBackgroundRefresh: boolean = false) => {
      const result = (await fetchRequests(
        false,
        isBackgroundRefresh
      )) as FetchResult | null;
      if (result) {
        const { formattedRequests, clientData } = result;

        // Set client data from initial query (eliminates N+1 queries)
        if (clientData) {
          setClientRiskStatus((prev) => ({
            ...prev,
            ...clientData.riskStatus,
          }));
          setClientSalesConditions((prev) => ({
            ...prev,
            ...clientData.salesConditions,
          }));
        }

        // Fetch remaining related data
        await fetchClientNames(formattedRequests, clientData);
        await fetchSellerProfiles(formattedRequests);
        await fetchApproverProfiles(formattedRequests);
        await fetchPaymentCounts(formattedRequests);
        await fetchSalesRequestItems(formattedRequests);
      }
    },
    [
      fetchRequests,
      fetchClientNames,
      fetchSellerProfiles,
      fetchApproverProfiles,
      fetchPaymentCounts,
      fetchSalesRequestItems,
    ]
  );

  // Load the next page (infinite scroll). Mirrors fetchSalesRequests so the
  // appended page's client names / related data are populated too — otherwise
  // orders from page >= 2 render "Cliente sin nombre" because loadMore only
  // appended rows without updating the clientNames map.
  const loadMoreSalesRequests = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;
    const result = (await fetchRequests(true)) as FetchResult | null;
    if (result) {
      const { formattedRequests, clientData } = result;

      if (clientData) {
        setClientRiskStatus((prev) => ({
          ...prev,
          ...clientData.riskStatus,
        }));
        setClientSalesConditions((prev) => ({
          ...prev,
          ...clientData.salesConditions,
        }));
      }

      await fetchClientNames(formattedRequests, clientData);
      await fetchSellerProfiles(formattedRequests);
      await fetchApproverProfiles(formattedRequests);
      await fetchPaymentCounts(formattedRequests);
      await fetchSalesRequestItems(formattedRequests);
    }
  }, [
    isLoadingMore,
    hasMore,
    fetchRequests,
    fetchClientNames,
    fetchSellerProfiles,
    fetchApproverProfiles,
    fetchPaymentCounts,
    fetchSalesRequestItems,
  ]);

  // Ref to track the latest fetchSalesRequests function to avoid stale closures in realtime subscriptions
  const fetchSalesRequestsRef = useRef(fetchSalesRequests);
  useEffect(() => {
    fetchSalesRequestsRef.current = fetchSalesRequests;
  }, [fetchSalesRequests]);

  // Refs for debouncing realtime updates and preventing concurrent fetches
  const realtimeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const isFetchingRef = useRef(false);
  const movedTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  );
  // Sales requests the current user just changed (id → expiry timer). Only
  // these get the Kanban move animation, so the board doesn't flash on the
  // hundreds of daily status changes other people make.
  const myChangeIdsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  );
  // Status-change events that arrived before registration (id → destination
  // status + expiry timer). Slow action chains (e.g. Aprobar Documento awaits
  // the create-pick-task edge function before calling onOrderStatusChange)
  // register AFTER the realtime event lands, so buffer the event and flag it
  // when the matching registration shows up.
  const pendingEventsRef = useRef<
    Map<string, { status: string; timer: ReturnType<typeof setTimeout> }>
  >(new Map());

  // Flag a sales request as recently moved and auto-clear after the animation
  // window, so the Kanban can highlight it as it arrives in its new column.
  const flagMoved = useCallback((id: string, status: string) => {
    setMovedStatuses((prev) => {
      const next = new Map(prev);
      next.set(id, status);
      return next;
    });
    const existing = movedTimersRef.current.get(id);
    if (existing) clearTimeout(existing);
    movedTimersRef.current.set(
      id,
      setTimeout(() => {
        setMovedStatuses((prev) => {
          if (!prev.has(id)) return prev;
          const next = new Map(prev);
          next.delete(id);
          return next;
        });
        movedTimersRef.current.delete(id);
      }, 2500)
    );
  }, []);

  // Record that the current user initiated a status change on this order, so
  // the realtime handler animates only this change (not other users'). If the
  // realtime event already arrived (buffered), flag it now; otherwise register
  // and let the event side flag it. Expires on its own if the event never comes.
  const registerMyStatusChange = useCallback(
    (id: string) => {
      const buffered = pendingEventsRef.current.get(id);
      if (buffered) {
        clearTimeout(buffered.timer);
        pendingEventsRef.current.delete(id);
        flagMoved(id, buffered.status);
        return;
      }
      const existing = myChangeIdsRef.current.get(id);
      if (existing) clearTimeout(existing);
      myChangeIdsRef.current.set(
        id,
        setTimeout(() => myChangeIdsRef.current.delete(id), 6000)
      );
    },
    [flagMoved]
  );

  // Fetch a single sales request's items
  const fetchItemsForSalesRequest = async (salesRequestId: string) => {
    try {
      // Always fetch fresh data for this specific sales request
      const { data, error } = await supabase
        .from("sales_request_items")
        .select(
          `
          id,
          sales_request_id,
          product_id,
          quantity,
          unit_price,
          discount_percent,
          created_at,
          products:product_id (
            short_name,
            long_name,
            unit_code,
            base_measurement_unit,
            group_description
          )
        `
        )
        .eq("sales_request_id", salesRequestId);

      if (error) {
        console.error("Error fetching sales request items:", error);
        throw error;
      }

      const formattedItems = (data || []).map((item) => ({
        id: item.id,
        salesRequestId: item.sales_request_id,
        productId: item.product_id,
        quantity: item.quantity,
        unitPrice: item.unit_price,
        discountPercent: item.discount_percent ?? undefined,
        createdAt: item.created_at,
        unit:
          (item.products as any)?.unit_code ||
          (item.products as any)?.base_measurement_unit,
        group: (item.products as any)?.group_description ?? undefined,
      }));

      // Update the state with the fetched items
      setSalesRequestItems((prev) => ({
        ...prev,
        [salesRequestId]: formattedItems,
      }));

      return formattedItems;
    } catch (error) {
      console.error("Error in fetchItemsForSalesRequest:", error);
      return [];
    }
  };

  useEffect(() => {
    const handleRealtimeUpdates = () => {
      const channel = supabase
        .channel(`sales-request-changes-${instanceId}`)
        .on(
          "postgres_changes",
          {
            event: "*", // Listen for all events (INSERT, UPDATE, DELETE)
            schema: "public",
            table: "sales_request",
          },
          async (payload) => {
            // Debounce realtime updates to prevent cascade of concurrent fetches
            if (realtimeDebounceRef.current) {
              clearTimeout(realtimeDebounceRef.current);
            }

            realtimeDebounceRef.current = setTimeout(async () => {
              // Prevent concurrent fetches
              if (isFetchingRef.current) {
                return;
              }

              isFetchingRef.current = true;
              try {
                // Reload all data to ensure related tables are up-to-date
                // Use ref to avoid stale closure issues
                // Pass true for isBackgroundRefresh to prevent clearing display
                await fetchSalesRequestsRef.current(true);
              } finally {
                isFetchingRef.current = false;
              }
            }, 500); // 500ms debounce

            // Show notifications for different change types
            if (payload.eventType === "UPDATE") {
              const newStatus = payload.new.status;
              const oldStatus = payload.old.status;
              const newPaid = payload.new.paid;
              const oldPaid = payload.old.paid;
              const newTotalAmount = payload.new.total_amount;
              const oldTotalAmount = payload.old.total_amount;

              const getStatusText = (status: string) => {
                switch (status) {
                  case "pending":
                    return "Pendiente";
                  case "accepted":
                    return "Aprobada";
                  case "rejected":
                    return "Rechazada";
                  case "in_transit":
                    return "En tránsito";
                  case "delivered":
                    return "Entregada";
                  default:
                    return status;
                }
              };

              // Notification for status changes
              if (newStatus !== oldStatus) {
                // Animate only if this user made the change (self-only, so the
                // board doesn't flash on everyone else's status changes).
                if (myChangeIdsRef.current.has(payload.new.id)) {
                  flagMoved(payload.new.id, newStatus);
                  const timer = myChangeIdsRef.current.get(payload.new.id);
                  if (timer) clearTimeout(timer);
                  myChangeIdsRef.current.delete(payload.new.id);
                } else {
                  // Not registered (yet) — buffer the event so a registration
                  // arriving after a slow action chain still animates it.
                  const prior = pendingEventsRef.current.get(payload.new.id);
                  if (prior) clearTimeout(prior.timer);
                  pendingEventsRef.current.set(payload.new.id, {
                    status: newStatus,
                    timer: setTimeout(
                      () => pendingEventsRef.current.delete(payload.new.id),
                      10000
                    ),
                  });
                }
                toast({
                  title: "Actualización de estado",
                  description: `Nota de venta ${payload.new.id.slice(0, 8)}... cambió de ${getStatusText(oldStatus)} a ${getStatusText(newStatus)}`,
                });
              }
              // Notification for payment changes
              else if (newPaid !== oldPaid) {
                toast({
                  title: "Actualización de pago",
                  description: newPaid
                    ? `Nota de venta ${payload.new.id.slice(0, 8)}... ha sido marcada como pagada`
                    : `Nota de venta ${payload.new.id.slice(0, 8)}... ha sido marcada como pendiente de pago`,
                });
              }
              // Notification for amount changes
              else if (newTotalAmount !== oldTotalAmount) {
                toast({
                  title: "Actualización de monto",
                  description: `Monto de la nota de venta ${payload.new.id.slice(0, 8)}... ha sido actualizado`,
                });
              }
            } else if (payload.eventType === "INSERT") {
              toast({
                title: "Nueva nota de venta",
                description: `Se ha creado una nueva nota de venta con ID ${payload.new.id.slice(0, 8)}...`,
              });
            } else if (payload.eventType === "DELETE") {
              toast({
                title: "Nota de venta eliminada",
                description: `Se ha eliminado la nota de venta con ID ${payload.old.id.slice(0, 8)}...`,
              });
            }
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    };

    const unsubscribe = handleRealtimeUpdates();

    return () => {
      if (unsubscribe) unsubscribe();
      movedTimersRef.current.forEach((timer) => clearTimeout(timer));
      movedTimersRef.current.clear();
      myChangeIdsRef.current.forEach((timer) => clearTimeout(timer));
      myChangeIdsRef.current.clear();
      pendingEventsRef.current.forEach((entry) => clearTimeout(entry.timer));
      pendingEventsRef.current.clear();
    };
  }, []);

  useEffect(() => {
    const handleClientUpdates = () => {
      const channel = supabase
        .channel(`client-changes-${instanceId}`)
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "clients",
          },
          async (payload) => {
            console.log("Client updated:", payload);
            // If risk status changed
            if (payload.new.risk_checked !== payload.old.risk_checked) {
              setClientRiskStatus((prev) => ({
                ...prev,
                [payload.new.id]: payload.new.risk_checked,
              }));
            }

            // If sales condition changed
            if (
              payload.new.sales_condition_id !== payload.old.sales_condition_id
            ) {
              setClientSalesConditions((prev) => ({
                ...prev,
                [payload.new.id]: payload.new.sales_condition_id,
              }));
            }
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    };

    const unsubscribe = handleClientUpdates();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // Add realtime subscription for sales_request_items table
  useEffect(() => {
    const handleSalesRequestItemsUpdates = () => {
      const channel = supabase
        .channel(`sales-request-items-changes-${instanceId}`)
        .on(
          "postgres_changes",
          {
            event: "*", // Listen for all events (INSERT, UPDATE, DELETE)
            schema: "public",
            table: "sales_request_items",
          },
          async (payload) => {
            console.log("Sales request items update received:", payload);

            // If items were added/updated/deleted, reload the items for the affected sales request
            const salesRequestId =
              (payload.new as any)?.sales_request_id ||
              (payload.old as any)?.sales_request_id;

            if (salesRequestId) {
              // Fetch updated items for this sales request
              try {
                const { data, error } = await supabase
                  .from("sales_request_items")
                  .select(
                    `
                    id,
                    sales_request_id,
                    product_id,
                    quantity,
                    unit_price,
                    discount_percent,
                    created_at,
                    products:product_id (
                      group_description
                    )
                  `
                  )
                  .eq("sales_request_id", salesRequestId);

                if (error) throw error;

                if (data) {
                  // Update just the items for this specific sales request
                  setSalesRequestItems((prev) => ({
                    ...prev,
                    [salesRequestId]: data.map((item) => ({
                      id: item.id,
                      salesRequestId: item.sales_request_id,
                      productId: item.product_id,
                      quantity: item.quantity,
                      unitPrice: item.unit_price,
                      discountPercent: item.discount_percent ?? undefined,
                      createdAt: item.created_at,
                      group:
                        (item.products as any)?.group_description ?? undefined,
                    })),
                  }));
                }
              } catch (error) {
                console.error(
                  "Error fetching updated sales request items:",
                  error
                );
              }
            }
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    };

    const unsubscribe = handleSalesRequestItemsUpdates();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  useEffect(() => {
    fetchSalesRequests();
  }, []);

  const getOrdersByStatus = (status: string) =>
    salesRequests.filter((req) => req.status === status);

  const pendingOrders = getOrdersByStatus("pending");
  const inTransitOrders = getOrdersByStatus("in_transit");
  const approvedOrders = getOrdersByStatus("accepted").concat(inTransitOrders);

  return {
    isLoading,
    isLoadingMore,
    hasMore,
    salesRequests,
    salesRequestItems,
    clientNames,
    clientInfo,
    clientRiskStatus,
    clientSalesConditions,
    paymentCounts,
    getSellerInfo,
    getApproverRejecterInfo,
    fetchSalesRequests,
    loadMoreSalesRequests,
    fetchItemsForSalesRequest,
    movedStatuses,
    registerMyStatusChange,
    pendingOrders,
    inTransitOrders,
    approvedOrders,
  };
};
